import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ModalService } from 'mbs-ui-kit';
import { StorageDestinationsFacade } from 'mbs-ui-shared@facades/storage-destinations.facade';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { from, of, switchMap } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import {
  ButtonComponent,
  CardComponent,
  CardGroupComponent,
  SwitcherComponent,
  TagsInputComponent,
  TextEllipsisComponent
} from 'mbs-ui-kit';

import { ReactiveFormsModule } from '@angular/forms';
import { CreationMode } from '@components/storage-accounts/add-storage-account-wizard/add-storage-account-wizard.constants';

@UntilDestroy()
@Component({
  selector: 'mbs-amazon-advanced-settings-step',
  templateUrl: './amazon-advanced-settings.component.html',
  styleUrls: ['../../add-storage-account-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    CardComponent,
    CardGroupComponent,
    ReactiveFormsModule,
    SwitcherComponent,
    TagsInputComponent,
    TextEllipsisComponent,
    I18NextPipe
  ]
})
export class AmazonAdvancedSettingsComponent implements OnInit {
  private modalService = inject(ModalService);
  private i18N = inject(I18NextPipe);
  private storageService = inject(StorageAccountsService);
  private destinationsFacade = inject(StorageDestinationsFacade);

  public useSpecificBucketsControl: FormControl;
  public specificBucketsControl: FormControl;
  public bucketNames = null;
  public specificBucketsCache = signal([]);
  public specificBuckets = signal([]);
  public loading = signal(false);
  public isModalOpened = signal(false);
  // for edit mode only
  public existingBuckets = null;
  public preparedBucketsValue = computed(() => {
    const buckets = this.isModalOpened() ? this.specificBucketsCache() : this.specificBuckets();

    return buckets?.map(({ name }) => name).join(', ');
  });

  @Input() wizardForm: FormGroup;
  @Input() mode: CreationMode;
  @ViewChild('specifyBucketsModalContent', { static: true, read: TemplateRef }) specifyBucketsModalContent: TemplateRef<any>;

  ngOnInit() {
    this.useSpecificBucketsControl = this.wizardForm.get('arnRoleOptions.restrictBuckets') as FormControl;
    this.specificBucketsControl = this.wizardForm.get('arnRoleOptions.specificBuckets') as FormControl;

    this.useSpecificBucketsControl.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      if (val && this.mode === CreationMode.Edit && !this.existingBuckets) {
        this.loading.set(true);

        this.destinationsFacade
          .getStorageAccountDestinations(this.wizardForm.get('storageAccount')?.value.id)
          .pipe(finalize(() => this.loading.set(false)))
          .subscribe((destinations) => {
            this.existingBuckets = destinations.data?.map((dest) => dest.bucket.bucketName);

            this.specificBucketsControl.patchValue(
              this.existingBuckets.map((name) => ({
                name,
                disabled: true,
                tooltip: this.i18N.transform('storages:addStorageAccountWizard.advancedSettingsStep.disabledItemTooltip')
              }))
            );
          });
      }
    });

    this.specificBucketsControl.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      this.specificBuckets.set(val);
    });
  }

  public onBucketsButtonClick() {
    !this.bucketNames && this.loading.set(true);
    const req$ = this.bucketNames
      ? of(this.bucketNames)
      : this.storageService.getBucketsNames({
          storageAccountId: this.wizardForm.get('storageAccount')?.value?.id,
          fromWizard: true,
          skipError: false
        });

    this.specificBucketsCache.set([...this.specificBucketsControl.value]);

    req$
      .pipe(
        tap((res) => {
          this.loading.set(false);
          if (!this.bucketNames) {
            this.bucketNames = res.map((bucket) => ({ name: bucket?.bucketName }));
          }
          this.isModalOpened.set(true);
        }),
        switchMap(() => {
          return from(this.openModal()).pipe(catchError(() => of(false)));
        })
      )
      .subscribe((save) => {
        this.isModalOpened.set(false);
        if (!save) {
          this.specificBucketsControl.patchValue(this.specificBucketsCache());
        }

        this.specificBucketsCache.set([]);
      });
  }

  private openModal() {
    return this.modalService.open(
      {
        header: {
          title: this.i18N.transform(
            this.specificBucketsControl?.value?.length
              ? 'storages:addStorageAccountWizard.advancedSettingsStep.editBuckets'
              : 'storages:addStorageAccountWizard.advancedSettingsStep.selectBuckets'
          )
        },
        footer: {
          okButton: {
            type: 'primary',
            text: this.i18N.transform('shared:buttons.save', { format: 'capitalize' })
          },
          cancelButton: {
            type: 'secondary',
            text: this.i18N.transform('shared:buttons.cancel', { format: 'capitalize' })
          }
        }
      },
      this.specifyBucketsModalContent
    );
  }
}
